import styled, { css } from 'styled-components';
import * as bp from 'view/styles/breakpoints';
import * as elevation from 'view/styles/elevation';
import * as fonts from 'view/styles/fonts';
import * as grid from 'view/styles/grid';
import * as mixins from 'view/styles/mixins';
import * as transitions from 'view/styles/transitions';
import * as zIndex from 'view/styles/z-index';
import { SpotlightPulse } from '@atlaskit/onboarding';
import { colors } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';
import { UnstyledButton } from '@atlassian/help-center-common-component/unstyled-button';

export const EXPAND_TIME_MS = transitions.speedMs.medium;
export const SIDEBAR_WIDTH_LARGE_MULTIPLIER: number = 50;
export const SIDEBAR_WIDTH_LARGE_VIEWPORT = grid.multiple(SIDEBAR_WIDTH_LARGE_MULTIPLIER).px;
export const SIDEBAR_HEIGHT_SMALL_VIEWPORT = '40vh';
const TAG_BUTTON_HEIGHT = grid.unitless * 5;

// eslint-disable-next-line rulesdir/no-styled-export
export const ButtonLabel = styled.span`
    padding-left: ${grid.unitless / 2}px;
    padding-bottom: ${grid.unitless / 2}px;
`;

// eslint-disable-next-line rulesdir/no-styled-export
export const CustomizeContainer = styled.aside<transitions.TransitionProps>`
    ${transitions.slideInOutFromTop(EXPAND_TIME_MS)};
    ${elevation.e400};
    /* Set flex so the InnerContainer and ActionButtons are positioned */
    /* InnerContainer will fill up as much height as possible, ActionButtons */
    /* Will always be at the bottom of the SidebarContainer. */
    display: flex;
    position: fixed;
    z-index: ${zIndex.pushingCustomizeSidebarSmall};
    top: 0;
    width: 100%;
    height: ${SIDEBAR_HEIGHT_SMALL_VIEWPORT};
    background-color: ${token('elevation.surface.overlay', colors.N0)};

    ${bp.fromMedium.css`
        ${transitions.slideInOutFromRight(EXPAND_TIME_MS)};
        right: 0;
        width: ${SIDEBAR_WIDTH_LARGE_VIEWPORT};
        height: 100vh;
        z-index: ${zIndex.pushingCustomizeSidebarLarge};
    `};
`;

// eslint-disable-next-line rulesdir/no-styled-export
export const InnerContainer = styled.div`
    overflow-y: auto;
    padding: ${token('space.300', '24px')};
    /* Take up all width inside the CustomizeContainer */
    width: 100%;

    ${bp.fromLarge.css`
        ${mixins.borderBox}
        max-width: ${SIDEBAR_WIDTH_LARGE_VIEWPORT};
    `};
`;

// eslint-disable-next-line rulesdir/no-styled-export
export const TagButton = styled(UnstyledButton)`
    ${mixins.flexCenter};
    ${fonts.regular};
    height: ${TAG_BUTTON_HEIGHT}px;
    color: ${token('color.text.subtle', colors.N800)};
    background: ${token('elevation.surface.overlay', colors.N0)};
    /* Offset the icon padding */
    padding: 0 ${token('space.200', '16px')} 0 ${grid.unitless + 2}px;
    /* Reenable pointer events just for the button. */
    pointer-events: auto;
    border-radius: 0 0 ${mixins.borderRadiusValue} ${mixins.borderRadiusValue};
    border: 1px solid ${token('color.border', colors.N30)};
    border-top-color: transparent;
    flex-shrink: 0;
    /* Center the button horizontally */
    margin-left: auto;
    margin-right: auto;

    > :not(:last-child) {
        margin-right: ${token('space.050', '4px')};
    }

    ${bp.fromMedium.css`
        border-bottom-color: transparent;
        border-top-color: ${token('color.border', colors.N30)};
        border-radius: ${mixins.borderRadiusValue} ${mixins.borderRadiusValue} 0 0;
        /* Offsets the rotated button all the way to the left of the container */
        /* IE11 fix: min-width required to display button text */
        min-width: ${grid.multiple(17).px};
    `};

    :disabled {
        cursor: default;
    }
`;

// eslint-disable-next-line rulesdir/no-styled-export
export const TagButtonContainer = styled.div`
    position: absolute;
    left: 0;
    right: 0;
    bottom: -${TAG_BUTTON_HEIGHT}px;
    /* Block pointer events so users can click through the div. */
    /* We reenable them in the TagButton */
    pointer-events: none;
    box-shadow: none;

    ${bp.fromMedium.css`
        padding-top: ${grid.multiple(20).px};
        left: -${TAG_BUTTON_HEIGHT}px;
        top: 0;
        bottom: 0;
        right: auto;
        transform: translateX(1px);
    `};
`;

const buttonTransformStyle = css`
    position: static;
    border-radius: 3px;
    height: ${TAG_BUTTON_HEIGHT}px;

    ${bp.fromMedium.css`
transform: rotate(-90deg);
transform-origin: ${TAG_BUTTON_HEIGHT * 2}px ${TAG_BUTTON_HEIGHT * 2}px;
`};
`;

// eslint-disable-next-line rulesdir/no-styled-export
export const Pulse = styled(SpotlightPulse)`
    ${buttonTransformStyle}
`;

// eslint-disable-next-line rulesdir/no-styled-export
export const WithoutPulse = styled.div`
    ${buttonTransformStyle}
`;
